import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import React from 'react';
import Drawer, { DrawerProps } from '@app/component/overlays/drawer/Drawer';
import {
	useFindMySavedMarketStateFilters, useMakeDefaultSavedMarketStateFilter, useRemoveSavedMarketStateFilter,
} from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterServiceHooks';
import _isEmpty from 'lodash/isEmpty';
import Message from '@app/component/message/Message';
import SavedMarketStateFilter from '@app/model/market_state/_saved_filters/SavedMarketStateFilter';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import {
	useMarketStateFilterInputTools,
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import Tag from '@app/component/tag/Tag';

type Props = {
	companyHotel: CompanyHotel
} & Omit<DrawerProps, 'title'>;

const DashboardHeaderSavedFiltersListDrawer = React.memo(
	function SavedFiltersListDrawer({ companyHotel, ...props }: Props) {

		const { filter, setFilters } = useMarketStateFilterInputTools();

		const { data: savedFilters, isSuccess, isError } = useFindMySavedMarketStateFilters(companyHotel.id);

		const removeSavedMarketStateFilter = useRemoveSavedMarketStateFilter();
		const makeDefaultSavedMarketStateFilter = useMakeDefaultSavedMarketStateFilter();
		const toaster = useToaster();

		const onRemove = (filter: SavedMarketStateFilter) => {
			removeSavedMarketStateFilter.mutate({ companyHotelId: companyHotel.id, id: filter.id }, {
				onSuccess: () => {
					toaster.success('Saved Filters', 'Filter removed successfully');
				},
			});
		};

		const onMakeDefault = (filter: SavedMarketStateFilter) => {
			makeDefaultSavedMarketStateFilter.mutate({ companyHotelId: companyHotel.id, id: filter.id }, {
				onSuccess: () => {
					toaster.success('Saved Filters', 'Default filter set successfully');
				},
			});
		};

		const onApply = (savedFilter: SavedMarketStateFilter) => {
			const msFilter = { ...savedFilter.filter, competitorTagIds: filter.competitorTagIds };
			setFilters(msFilter);
			toaster.success('Saved Filters', 'Filter applied successfully');
		};

		return <Drawer title="Saved Filters" {...props}>
			<div className="mt-2 px-6 py-5">
				{isSuccess && _isEmpty(savedFilters) && <Message state="info">No saved filters found</Message>}
				{isError && <Message state="error">Loading saved filters failed!</Message>}
				{isSuccess && !_isEmpty(savedFilters) && <>

					<div className="text-sm text-pretty mb-4">
						<p className="font-semibold tracking-tight text-gray-900">
							See below all your custom filters
						</p>
						<ul className="mt-2 ml-4 list-disc space-y-1 text-gray-500">
							<li>Setting as “Default” a filter ensures this filter is automatically applied when you open the app, serving as your “home view” so you can jump straight to work.</li>
						</ul>
					</div>

					<div>
						<div
							className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
							<div className="px-4 py-3"><h6 className="text-sm font-semibold text-gray-900">My Filters</h6></div>
							<div className="px-4 py-5 space-y-2">
								{savedFilters!.map((filter) => <div key={filter.id}
																										className="flex items-center justify-between rounded-md bg-white px-3 py-2 ring-1 ring-black ring-opacity-5 text-xs">
									<div>
										<span className="font-medium text-gray-700 mr-2">{filter.name}</span>
										{filter.isDefault && <Tag state="success">Default</Tag>}
									</div>
									<div className="ml-4 flex flex-shrink-0 items-start space-x-2">
										<button type="button" className="text-red-600 font-semibold hover:text-red-900"
														onClick={() => onRemove(filter)}>Delete
										</button>
										<span className="text-gray-300">|</span>
										{!filter.isDefault && <>
											<button type="button" className="text-blue-600 font-semibold hover:text-blue-900"
															onClick={() => onMakeDefault(filter)}>Set as Default
											</button>
											<span className="text-gray-300">|</span>
										</>}
										<button className="font-semibold text-blue-600 hover:text-blue-900"
														onClick={() => onApply(filter)}>Apply
										</button>
									</div>
								</div>)}

							</div>
						</div>
					</div>
				</>}
			</div>
		</Drawer>;
	},
);

export default DashboardHeaderSavedFiltersListDrawer;